import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ChakraProvider, Heading, Text, extendTheme } from '@chakra-ui/react';
import { Outlet, useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { LoadingIndicator } from 'ts/common/components';
import ClientPageBanner from 'ts/common/components/ClientPageBanner';
import ClientPageLayout from 'ts/common/components/ClientPageLayout';
import { getTheme } from 'ts/common/components/gallery';
import {
    type QuestionnaireError,
    isQuestionnaireError
} from 'ts/common/components/questionnaire/Questionnaire';
import { type IFetchHookResponse, useTranslate } from 'ts/common/hooks';
import { useBootstrapData, withBootstrapData } from 'client_react/bootstrap';
import { FetchMethod, useClientApiFetch } from 'client_react/bootstrap';

export type QuestionnaireContext = {
    questionnaire: SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire;
    setQuestionnaire: React.Dispatch<
        React.SetStateAction<
            SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire | undefined
        >
    >;
    fetchQuestionnaire: (
        email?: string
    ) => Promise<
        IFetchHookResponse<
            SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire | QuestionnaireError
        >
    >;
    loading: boolean;
    error: QuestionnaireError | null;
};

const QuestionnaireAppContent: React.FC = () => {
    const t = useTranslate('clientQuestionnaire');

    const { questionnaireId } = useParams<{ questionnaireId: string }>();
    const [error, setError] = useState<QuestionnaireError | null>(null);
    const [questionnaire, setQuestionnaire] = useState<
        SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire
    >();

    const { loading, performFetch } = useClientApiFetch<
        SpApi.Client.IQuestionnaire | QuestionnaireError
    >(`questionnaire/${questionnaireId}`, {
        defer: true,
        method: FetchMethod.GET
    });

    const fetchQuestionnaire = useCallback(
        async (email?: Nullable<string>) => {
            const response = await performFetch({
                search: email ? { email } : undefined
            });

            if (response && !isQuestionnaireError(response)) {
                setQuestionnaire(response);
                setError(null);

                if (email) {
                    sessionStorage.setItem(`questionnaire-email-${questionnaireId}`, email);
                }
            } else {
                setError(response);
            }

            return response;
        },
        [performFetch, questionnaireId]
    );

    useEffect(() => {
        const email = sessionStorage.getItem(`questionnaire-email-${questionnaireId}`);

        (async () => {
            const response = await fetchQuestionnaire(email);

            if (response?.status === 403) {
                sessionStorage.removeItem(`questionnaire-email-${questionnaireId}`);
                await fetchQuestionnaire();
            }
        })();
    }, [fetchQuestionnaire, questionnaireId]);

    const theme = useMemo(
        () => (questionnaire ? extendTheme(getTheme(questionnaire.brandTheme)) : extendTheme()),
        [questionnaire]
    );

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            {questionnaire ? (
                <ClientPageLayout
                    banner={<ClientPageBanner>{questionnaire.name}</ClientPageBanner>}
                    headingText={questionnaire.brandName}
                    showLegalLinks
                >
                    <Outlet
                        context={
                            {
                                questionnaire,
                                setQuestionnaire,
                                fetchQuestionnaire,
                                loading,
                                error
                            } satisfies QuestionnaireContext
                        }
                    />
                </ClientPageLayout>
            ) : loading ? (
                <LoadingIndicator />
            ) : error?.status === 404 ? (
                <Box>
                    <Heading as="h1">{t('questionnaireNotFound')}</Heading>
                    <Text>{t('questionnaireNotFoundMessage')}</Text>
                </Box>
            ) : (
                <Box>
                    <Heading as="h1">{t('errorLoadingQuestionnaireTitle')}</Heading>
                    <Text>{t('errorLoadingQuestionnaire')}</Text>
                </Box>
            )}
        </ChakraProvider>
    );
};

const QuestionnaireApp: React.FC = withBootstrapData(() => {
    const { translations } = useBootstrapData();

    const PageWithTranslations = useMemo(
        () => withI18NextTranslate(QuestionnaireAppContent, translations),
        [translations]
    );

    return <PageWithTranslations />;
});

export default QuestionnaireApp;
